import React from "react";
import { Chip } from "@mui/material";
import { insurerColor, shortLabel, realParts } from "../utils/policyParts";
import SegmentedFilter from "./SegmentedFilter";

/**
 * A small "OD" / "TP" / "PA" tag for a timeline entry or an error. `parts`
 * (the job's parts) lets it pick up the insurer's colour; without a match it
 * stays neutral. Renders nothing for a policy-wide entry (no `partKey`).
 */
export const PartTag = ({ partKey, parts }) => {
  if (!partKey) return null;
  const part = realParts(parts).find((p) => p.key === partKey);
  const color = part ? insurerColor(part.company).main : "#667085";
  return (
    <Chip
      label={part?.label || shortLabel(partKey)}
      size="small"
      variant="outlined"
      sx={{
        height: 18,
        fontSize: "10.5px",
        fontWeight: 700,
        color,
        borderColor: color,
        "& .MuiChip-label": { px: 0.75 },
      }}
    />
  );
};

/**
 * "All / OD / TP / PA" filter, as one segmented bar (SegmentedFilter). Only shown when the job has more than one part.
 * Entries with no part (hold, edit ...) are policy-wide, so callers keep them under every filter — see `matchesPart`.
 * `counts` (optional) puts each part's count in a badge; `label` (optional) is the caption above the bar.
 */
export const PartFilterChips = ({ parts, value, onChange, counts = {}, label, sx }) => {
  const list = realParts(parts).filter((p) => p.status !== "cancelled");
  if (list.length < 2) return null;

  const options = [{ key: "all", label: "All" }, ...list.map((p) => ({ key: p.key, label: p.label || shortLabel(p.key) }))].map(
    (o) => ({ ...o, count: counts[o.key] })
  );
  return <SegmentedFilter label={label} options={options} value={value} onChange={onChange} testId="part-filter" sx={sx} />;
};

/** A policy-wide entry (no `part`) shows under every filter. */
export const matchesPart = (entry, filter) =>
  filter === "all" ||
  !entry?.part ||
  // a change made to several parts at once is stored as "od,tp" (KYC files) - it belongs to each of them
  String(entry.part).split(",").map((k) => k.trim()).includes(filter);
